// ============================================================
// 관리자 알림 Vuex 모듈 (notifications)
// 상단바 벨에 표시할 개별 알림(강사 권한 요청, 미답변 문의 등)을 관리 — 프론트 전용
// - 별도 백엔드 알림 API 없이 기존 목록 API에서 파생 (docs/관리자_알림_구조.md 참고)
// - 폴링 없음: 로그인 직후 / 대시보드 진입 / 벨 클릭 시에만 조회
// - 읽음 상태는 localStorage 보관 (브라우저별 유지, 관리자 간 공유 안 됨)
// - 대기 목록에서 빠지면(요청 처리, 문의 답변 등) 알림도 자동 소멸
// ============================================================

import axios from "axios";
import CONF from "@/Config.js";
import { parseServerDateTime } from "@/utils/datetime";

const LS_KEY_READ = "pineapple.notificationsRead"; // { readKeys: ["instructor_request:12", ...] }
const FETCH_LIMIT = 10; // 타입별 최대 표시 건수

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("access_token")}`,
});

// 알림 타입별 정의 — 새 알림 타입은 여기에만 추가하면 됨
// fetchItems(): 대기 항목을 조회해 { items: [{ key, type, title, created_at }], total } 로 변환
//   - items: 벨 목록에 보일 항목 (is_read는 fetch 액션이 채움)
//   - total: 배지에 더할 해당 타입의 대기 건수
export const NOTIFICATION_TYPES = {
  instructor_request: {
    label: "강사 권한 요청",
    icon: "school",
    route: { name: "UsersIL" },
    async fetchItems() {
      const res = await axios.get(`${CONF.API_URL}/emp/instructor/requests`, {
        headers: authHeaders(),
        params: { status: "request", limit: FETCH_LIMIT, offset: 0 },
      });
      const items = (res.data.items || []).map((req) => ({
        key: `instructor_request:${req.idx}`,
        type: "instructor_request",
        title: `${req.name || "이름없음"}님이 강사 권한을 요청했습니다`,
        created_at: req.created_at,
      }));
      return { items, total: items.length };
    },
  },
  inquiry: {
    label: "미답변 문의",
    icon: "contact_support",
    route: { name: "HelpDeskInquiry" },
    async fetchItems() {
      // 서버 페이징 API — 대기(pending) 최신 10건만 받고 배지는 pagination.total
      const res = await axios.get(`${CONF.API_URL}/helpdesk/inquiries`, {
        headers: authHeaders(),
        params: { status: "pending", limit: FETCH_LIMIT, sort: "latest" },
      });
      const items = (res.data.inquiries || []).map((inq) => ({
        key: `inquiry:${inq.id}`,
        type: "inquiry",
        title: `${inq.user_name || "이름없음"}님의 문의: ${inq.subject || "(제목 없음)"}`,
        created_at: inq.created_at,
      }));
      return { items, total: res.data.pagination?.total ?? items.length };
    },
  },
};

// 서버 시각을 정렬용 epoch ms 로 (문의는 +00:00 UTC, 강사 요청은 표기 없는 KST)
const toEpoch = (value) => parseServerDateTime(value).date?.getTime() || 0;

const readLocalReadKeys = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(LS_KEY_READ));
    return Array.isArray(stored?.readKeys) ? stored.readKeys : [];
  } catch (e) {
    return [];
  }
};

const writeLocalReadKeys = (readKeys) => {
  try {
    localStorage.setItem(LS_KEY_READ, JSON.stringify({ readKeys }));
  } catch (e) {
    // ignore (private mode 등)
  }
};

const state = {
  items: [], // [{ key, type, title, created_at, is_read }]
  totalCount: 0, // 타입별 대기 건수 합 (목록에 안 보이는 대기분 포함)
  unreadCount: 0,
};

const getters = {
  items: (s) => s.items,
  badgeCount: (s) => s.totalCount, // 처리 대기 총 건수
  unreadCount: (s) => s.unreadCount,
  hasUnread: (s) => s.unreadCount > 0,
};

const mutations = {
  SET_ITEMS(s, { items, totalCount }) {
    s.items = items;
    s.totalCount = totalCount;
    s.unreadCount = items.filter((it) => !it.is_read).length;
  },
  MARK_READ(s, key) {
    const item = s.items.find((it) => it.key === key);
    if (item && !item.is_read) {
      item.is_read = true;
      s.unreadCount = s.items.filter((it) => !it.is_read).length;
    }
  },
  MARK_ALL_READ(s) {
    s.items.forEach((it) => {
      it.is_read = true;
    });
    s.unreadCount = 0;
  },
};

const actions = {
  // 전체 타입 알림 조회 — 실패해도 사용자에게 알리지 않음 (다음 조회 시점에 재시도)
  async fetch({ commit }) {
    const token = localStorage.getItem("access_token");
    if (!token || token === "null") return;

    const results = await Promise.allSettled(
      Object.values(NOTIFICATION_TYPES).map((type) => type.fetchItems())
    );
    results
      .filter((r) => r.status === "rejected")
      .forEach((r) => console.error("알림 조회 실패:", r.reason));

    const readKeys = readLocalReadKeys();
    const fulfilled = results.filter((r) => r.status === "fulfilled").map((r) => r.value);
    const totalCount = fulfilled.reduce((sum, v) => sum + v.total, 0);
    const items = fulfilled
      .flatMap((v) => v.items)
      .map((it) => ({ ...it, is_read: readKeys.includes(it.key) }))
      .sort((a, b) => toEpoch(b.created_at) - toEpoch(a.created_at));

    // 처리되어 목록에서 사라진 key는 localStorage에서 정리 (무한 증가 방지)
    // 일부 타입 조회가 실패한 경우에는 해당 타입 key가 오인 삭제되지 않도록 정리 생략
    if (results.every((r) => r.status === "fulfilled")) {
      const liveKeys = items.map((it) => it.key);
      const prunedReadKeys = readKeys.filter((k) => liveKeys.includes(k));
      if (prunedReadKeys.length !== readKeys.length) {
        writeLocalReadKeys(prunedReadKeys);
      }
    }

    commit("SET_ITEMS", { items, totalCount });
  },

  markRead({ commit }, key) {
    commit("MARK_READ", key);
    const readKeys = readLocalReadKeys();
    if (!readKeys.includes(key)) {
      writeLocalReadKeys([...readKeys, key]);
    }
  },

  markAllRead({ state, commit }) {
    commit("MARK_ALL_READ");
    const readKeys = readLocalReadKeys();
    const merged = [...new Set([...readKeys, ...state.items.map((it) => it.key)])];
    writeLocalReadKeys(merged);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
